import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageTitle, Btn } from "./ui";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STYLE = {
  published: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  scheduled: "border-amber-400/40 bg-amber-400/10 text-amber-200",
  queued: "border-sky-400/40 bg-sky-400/10 text-sky-200",
};
const key = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const buildGrid = (month) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
};

const Entry = ({ e, onDragStart }) => {
  const time = e.date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const movable = e.type !== "published";
  const body = <><span className="opacity-70">{time}</span> {e.title}</>;
  const cls = `block truncate rounded-md border px-1.5 py-1 text-[11px] leading-tight transition-colors hover:brightness-125 ${STYLE[e.type]} ${movable ? "cursor-grab active:cursor-grabbing" : ""}`;
  const drag = movable ? { draggable: true, onDragEnd: () => onDragStart(null), onDragStart: (ev) => { ev.dataTransfer.effectAllowed = "move"; ev.dataTransfer.setData("text/plain", e.id); onDragStart(e); } } : {};
  return e.link
    ? <Link to={e.link} title={movable ? `${e.title} — drag to another day` : e.title} className={cls} data-testid={`cal-entry-${e.id}`} {...drag}>{body}</Link>
    : <span title={e.title} className={cls} data-testid={`cal-entry-${e.id}`} {...drag}>{body}</span>;
};

export default function ContentCalendar() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [posts, setPosts] = useState([]);
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState("");
  const [expanded, setExpanded] = useState({});
  const load = () => {
    api.get("/admin/posts?kind=blog").then(({ data }) => setPosts(data));
    api.get("/admin/ai-queue").then(({ data }) => setQueue(data));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const drop = async (day) => {
    const e = dragging;
    setDragging(null);
    setOver("");
    if (!e || key(day) === key(e.date)) return;
    const when = new Date(day.getFullYear(), day.getMonth(), day.getDate(), e.date.getHours(), e.date.getMinutes());
    if (when <= new Date()) return toast.error("Can't move a post into the past");
    const iso = when.toISOString();
    if (e.type === "scheduled") setPosts((l) => l.map((p) => (p.id === e.id ? { ...p, scheduled_at: iso } : p)));
    else setQueue((l) => l.map((q) => (q.id === e.id ? { ...q, scheduled_at: iso } : q)));
    try {
      await api.patch(e.type === "scheduled" ? `/admin/posts/${e.id}/schedule` : `/admin/ai-queue/${e.id}`, { scheduled_at: iso });
      toast.success(`Moved to ${when.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`);
    } catch (err) { toast.error(errMsg(err)); load(); }
  };

  const byDay = useMemo(() => {
    const map = {};
    const add = (e) => { (map[key(e.date)] ||= []).push(e); };
    posts.forEach((p) => {
      if (p.status === "published" && p.published_at) add({ id: p.id, type: "published", title: p.title, date: new Date(p.published_at), link: `/admin/blogs/${p.id}` });
      if (p.status === "scheduled" && p.scheduled_at) add({ id: p.id, type: "scheduled", title: p.title, date: new Date(p.scheduled_at), link: `/admin/blogs/${p.id}` });
    });
    queue.filter((q) => q.status !== "done").forEach((q) => add({ id: q.id, type: "queued", title: q.topic, date: new Date(q.scheduled_at), link: "/admin/ai-queue" }));
    Object.values(map).forEach((l) => l.sort((a, b) => a.date - b.date));
    return map;
  }, [posts, queue]);

  const grid = buildGrid(month);
  const today = key(new Date());
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const inMonth = grid.filter((d) => d.getMonth() === month.getMonth()).reduce((a, d) => a + (byDay[key(d)]?.length || 0), 0);

  return (
    <div data-testid="admin-calendar">
      <PageTitle title="Content Calendar" sub="Published, scheduled and queued AI posts — drag a scheduled or queued post to another day to move it">
        <Link to="/admin/ai-queue" className="btn-outline !py-2" data-testid="cal-bulk-link">Bulk AI posts</Link>
        <Link to="/admin/blogs/new" className="btn-gold !py-2" data-testid="cal-new-post"><Plus size={15} /> New post</Link>
      </PageTitle>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Btn variant="outline" onClick={() => shift(-1)} aria-label="Previous month" data-testid="cal-prev"><ChevronLeft size={15} /></Btn>
        <h2 className="min-w-[180px] text-center font-serif text-xl text-white" data-testid="cal-month">{month.toLocaleDateString([], { month: "long", year: "numeric" })}</h2>
        <Btn variant="outline" onClick={() => shift(1)} aria-label="Next month" data-testid="cal-next"><ChevronRight size={15} /></Btn>
        <button onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} className="text-xs uppercase tracking-widest text-[#D4AF37]" data-testid="cal-today">Today</button>
        <span className="text-xs text-zinc-500" data-testid="cal-count">{inMonth} post{inMonth === 1 ? "" : "s"} this month</span>
        <div className="ml-auto flex flex-wrap gap-3 text-[11px] text-zinc-400">
          {Object.keys(STYLE).map((k) => <span key={k} className="flex items-center gap-1.5 capitalize"><span className={`h-2.5 w-2.5 rounded-sm border ${STYLE[k]}`} />{k}</span>)}
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#D4AF37]/20 bg-[#101117]">
        <div className="grid min-w-[760px] grid-cols-7">
          {DAYS.map((d) => <div key={d} className="border-b border-white/5 px-2 py-2 text-[10px] uppercase tracking-widest text-zinc-500">{d}</div>)}
          {grid.map((d) => {
            const k = key(d);
            const list = byDay[k] || [];
            const out = d.getMonth() !== month.getMonth();
            const past = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1) <= new Date();
            const canDrop = dragging && !past;
            return (
              <div key={k} className={`min-h-[110px] border-b border-r border-white/5 p-1.5 transition-colors ${out ? "bg-black/30 opacity-50" : ""} ${canDrop && over === k ? "!bg-[#D4AF37]/15 ring-1 ring-inset ring-[#D4AF37]" : ""} ${dragging && past ? "cursor-not-allowed" : ""}`}
                onDragOver={(ev) => { if (canDrop) { ev.preventDefault(); setOver(k); } }}
                onDragLeave={() => setOver((o) => (o === k ? "" : o))}
                onDrop={(ev) => { ev.preventDefault(); drop(d); }}
                data-testid={`cal-day-${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`}>
                <span className={`mb-1 inline-grid h-6 w-6 place-items-center rounded-full text-xs ${k === today ? "bg-[#D4AF37] font-bold text-black" : "text-zinc-400"}`}>{d.getDate()}</span>
                <div className="space-y-1">
                  {(expanded[k] ? list : list.slice(0, 3)).map((e) => <Entry key={`${e.type}-${e.id}`} e={e} onDragStart={(x) => { setDragging(x); if (!x) setOver(""); }} />)}
                  {list.length > 3 && (
                    <button type="button" onClick={() => setExpanded((s) => ({ ...s, [k]: !s[k] }))} className="px-1 text-[10px] text-[#D4AF37] hover:underline" data-testid={`cal-more-${k}`}>
                      {expanded[k] ? "Show less" : `+${list.length - 3} more`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
